import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  FormControl,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel,
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
  List,
  ListItem,
  InputAdornment,
  Stack,
  debounce,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Swal from "sweetalert2";
import {
  smartWoCreationSchema,
  SmartWoCreationFormData,
  defaultFormValues,
} from "./schema";
import {
  SmartWoCreationDialogProps,
  CustomerOption,
  CreateWorkOrderRequest,
} from "./types";
import { useCreateSmartWorkOrder } from "./api";
import {
  ButtonStyle,
  ButtonStyleOutlined,
  searchCustomerListStyle,
  searchCustomerListStyleTechnicianCalendar,
  searchListItem,
} from "../../QueueBoard/models/workOrderStyles";
import { useWorkOrderStore } from "../../store/zustandWorkOrderStore";
import { CustomerAPI } from "../../QueueBoard/api/customerApi";
import localStorageHelper from "../../../app/localStorage";
import { getAuditUserName } from "../../common/Utils/auditUser";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import packageJson from "../../../../package.json";
import JumpingLoader from "../../common/global-style/JumpingLoader";

// Helper component for required field label
const RequiredLabel: React.FC<{ label: string }> = ({ label }) => (
  <span>
    {label} <span style={{ color: "red" }}>*</span>
  </span>
);

/**
 * Smart Work Order Creation Dialog
 * Quick dialog for creating work orders with mandatory fields only
 * Uses react-hook-form with zod validation
 */
const SmartWoCreationDialog: React.FC<SmartWoCreationDialogProps> = ({
  open,
  onClose,
  preSelectedTechnicianId,
  preSelectedDate,
  onSuccess,
}) => {
  const theme = useTheme();

  // Get user info from localStorage
  const user = useMemo(() => localStorageHelper.getByKey("user"), []);
  const userName = useMemo(() => getAuditUserName(), []);

  // Get lookup data from zustand store (following existing codebase patterns)
  const {
    locations,
    woStatuses,
    woPriorityList,
    salesPersons,
    technicians,
    serviceCodes,
  } = useWorkOrderStore();

  // Helper functions to get default values (following WorkOrderDialogContainerMain pattern)
  const getDefaultSalesPersonIdentity = useCallback(() => {
    if (!user?.isSalesPerson) {
      return undefined;
    }
    const salesPerson = salesPersons.find(
      (person) => person.identity === user.identity,
    );
    return salesPerson ? salesPerson.identity : undefined;
  }, [user, salesPersons]);

  const getDefaultLocationIdentity = useCallback(() => {
    const location = locations.find(
      (loc) => loc.identity === user?.defaultLocationIdentity,
    );
    return location ? location.identity : undefined;
  }, [locations, user]);

  const getDefaultWoStatus = useCallback(() => {
    const statusView = JSON.parse(
      localStorage.getItem(`IsStatusView_${userName}`) || "false",
    );
    const statusIdentity = JSON.parse(
      localStorage.getItem("statusIdentity") || "null",
    );
    if (statusView && statusIdentity) {
      return (
        woStatuses.find((status) => status.identity === Number(statusIdentity))
          ?.identity || undefined
      );
    }
    return woStatuses.find((status) => status.isDefault)?.identity || undefined;
  }, [woStatuses, userName]);

  const getDefaultPriority = useCallback(() => {
    return (
      woPriorityList.find((priority) => priority.isDefault)?.identity ||
      undefined
    );
  }, [woPriorityList]);

  const getDefaultTechnicianIdentity = useCallback(() => {
    const isTechView = JSON.parse(
      localStorage.getItem(`IsTechView_${userName}`) || "false",
    );
    const techIdentity = JSON.parse(
      localStorage.getItem("techIdentity") || "null",
    );
    if (isTechView && techIdentity) {
      return (
        technicians?.find((tech) => tech.identity === Number(techIdentity))
          ?.identity || undefined
      );
    }
    return undefined;
  }, [technicians, userName]);

  // Customer search state
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");

  // Ref for customer search container to detect clicks outside
  const customerSearchRef = useRef<HTMLDivElement>(null);

  // React Hook Form setup with Zod resolver
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SmartWoCreationFormData>({
    resolver: zodResolver(smartWoCreationSchema),
    defaultValues: defaultFormValues,
    mode: "onBlur",
  });

  // Watch form values
  const watchCustomerIdentity = watch("customerIdentity");

  // Mutation hook for creating smart work order
  const createWorkOrderMutation = useCreateSmartWorkOrder();

  // Set default values when dialog opens (following WorkOrderDialogContainerMain pattern)
  useEffect(() => {
    if (open) {
      // Priority: preSelectedTechnicianId > tech view default > undefined
      const defaultTechId =
        preSelectedTechnicianId || getDefaultTechnicianIdentity();
      if (defaultTechId) {
        setValue("technicianIdentity", defaultTechId);
      }

      const defaultSalesPerson = getDefaultSalesPersonIdentity();
      if (defaultSalesPerson) {
        setValue("salesPersonIdentity", defaultSalesPerson);
      }

      const defaultLocation = getDefaultLocationIdentity();
      if (defaultLocation) {
        setValue("locationIdentity", defaultLocation);
      }

      const defaultStatus = getDefaultWoStatus();
      if (defaultStatus) {
        setValue("woStatusIdentity", defaultStatus);
      }

      const defaultPriority = getDefaultPriority();
      if (defaultPriority) {
        setValue("woPriorityIdentity", defaultPriority);
      }

      // Set work date from preSelectedDate when opened from technician card
      if (preSelectedDate) {
        setValue("workDate", preSelectedDate);
      }
    }
  }, [
    open,
    preSelectedTechnicianId,
    preSelectedDate,
    setValue,
    getDefaultSalesPersonIdentity,
    getDefaultLocationIdentity,
    getDefaultWoStatus,
    getDefaultPriority,
    getDefaultTechnicianIdentity,
  ]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset(defaultFormValues);
      setCustomers([]);
      setShowCustomerDropdown(false);
      setCustomerSearchTerm("");
    }
  }, [open, reset]);

  // Handle click outside customer search dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerSearchRef.current &&
        !customerSearchRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Debounced customer search
  const debouncedSearchCustomers = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (searchTerm.length < 2) {
          setCustomers([]);
          setShowCustomerDropdown(false);
          return;
        }

        setCustomerSearchLoading(true);
        try {
          const result = await CustomerAPI.searchCustomersByName(searchTerm);
          if (result) {
            setCustomers(
              result.map((customer) => ({
                identity: customer.identity,
                name: customer.name || "",
                number: customer.number || "",
              })),
            );
            setShowCustomerDropdown(true);
          } else {
            setCustomers([]);
            setShowCustomerDropdown(false);
          }
        } catch (error) {
          console.error("Error searching customers:", error);
          setCustomers([]);
        } finally {
          setCustomerSearchLoading(false);
        }
      }, 300),
    [],
  );

  // Handle customer search input change
  const handleCustomerSearchChange = (value: string) => {
    setCustomerSearchTerm(value);
    debouncedSearchCustomers(value);
  };

  // Handle customer selection
  const handleCustomerSelect = (customer: CustomerOption) => {
    setValue("customerIdentity", customer.identity, {
      shouldValidate: true,
    });
    setCustomerSearchTerm(`${customer.name} / ${customer.number}`);
    setShowCustomerDropdown(false);
    setCustomers([]);
  };

  // Handle form submission
  const onSubmit = async (
    data: SmartWoCreationFormData,
    actionType: "draft" | "commit",
  ) => {
    // Check user permissions
    if (!user?.hasWorkOrderAccess) {
      await Swal.fire({
        icon: "error",
        title: "Access Denied",
        text: "You do not have permission to create work order.",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
      return;
    }

    const dtNow = new Date().toISOString();

    // Build request payload
    // Map actionType to status: "commit" -> "committed", "draft" -> "draft"
    const status: "draft" | "committed" =
      actionType === "commit" ? "committed" : "draft";

    const auditUser = user?.userName?.trim() || getAuditUserName();
    const requestPayload: CreateWorkOrderRequest = {
      customerIdentity: data.customerIdentity,
      locationIdentity: data.locationIdentity,
      woStatusIdentity: data.woStatusIdentity,
      woPriorityIdentity: data.woPriorityIdentity,
      salesPersonIdentity: data.salesPersonIdentity,
      technicianIdentity: data.technicianIdentity,
      status: status,
      actionType: actionType,
      createdBy: auditUser,
      createdDate: dtNow,
      modifiedBy: auditUser,
      modifiedDate: dtNow,
      workDate: data.workDate, // Work date for scheduling (from technician card)
      preSelectedDate: preSelectedDate, // Keep for backward compatibility
      segments: [
        {
          displayText: data.segment.displayText,
          serviceCodeIdentity: data.segment.serviceCodeIdentity,
        },
      ],
    };

    try {
      // Call API to create work order
      const response =
        await createWorkOrderMutation.mutateAsync(requestPayload);

      // Show success message with response message if available
      await Swal.fire({
        title: "Success",
        html:
          response.message ||
          `Work order ${response.documentNumber} has been created successfully`,
        icon: "success",
        confirmButtonColor: "#2371E7",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      // Log amplitude event
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        actionType === "draft" ? "SaveDraftWorkOrder" : "CommitWorkOrder",
        {
          elapsed_ms: "124",
          title: "Smart WO Creation Dialog",
          componentName: "smartWoCreationDialog.tsx",
          version: packageJson.version,
        },
      );

      // Reset form and close dialog
      reset(defaultFormValues);
      onClose();

      // Call onSuccess callback to notify parent component
      onSuccess?.();

      // Trigger refresh of work orders list
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("refreshWorkOrdersTechCalendar"));
      }
    } catch (error) {
      console.error("Error creating work order:", error);
      await Swal.fire({
        title: "Error",
        html: `Work order creation failed`,
        // : ${
        //   error instanceof Error ? error.message : "Unknown error"
        // }`,
        icon: "error",
        confirmButtonColor: "#2371E7",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
    }
  };

  // Helper to get field error message
  const getErrorMessage = (
    fieldName:
      | keyof SmartWoCreationFormData
      | `segment.${keyof SmartWoCreationFormData["segment"]}`,
  ) => {
    if (fieldName.startsWith("segment.")) {
      const segmentField = fieldName.split(
        ".",
      )[1] as keyof SmartWoCreationFormData["segment"];
      return errors.segment?.[segmentField]?.message;
    }
    return errors[fieldName as keyof SmartWoCreationFormData]?.message;
  };

  // Transform lookup data for dropdowns
  const locationOptions = locations.map((loc) => ({
    identity: loc.identity,
    displayText: loc.displayText,
  }));

  const statusOptions = woStatuses.map((status) => ({
    identity: status.identity,
    displayText: status.displayText,
  }));

  const priorityOptions = woPriorityList.map((priority) => ({
    identity: priority.identity,
    displayText: priority.displayText,
  }));

  const salesPersonOptions = salesPersons.map((sp) => ({
    identity: sp.identity,
    displayText: sp.userName,
  }));

  const technicianOptions = technicians.map((tech) => ({
    identity: tech.identity,
    displayText: tech.userName,
  }));

  const serviceCodeOptions = serviceCodes.map((code) => ({
    identity: code.identity,
    code: code.serviceCode,
    description: code.description,
    displayText: `${code.serviceCode} - ${code.description}`,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: "auto",
          maxHeight: "90vh",
          position: "relative",
          overflow: "hidden",
        },
      }}
    >
      {/* Dialog Header - Following WorkOrderDialogContainerMain.tsx pattern */}
      <DialogTitle
        sx={{
          m: 0,
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : "#181F2A",
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.contrastText
              : "#fff",
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            pl="15px"
            sx={{
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "16px",
            }}
          >
            Quick Create Work Order
          </Typography>
          <IconButton sx={{ color: "white" }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ p: 3, overflow: "auto" }}>
        <Box>
          {/* Section 1: Work Order Mandatory Info */}
          <Box sx={{ mb: 4, mt: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.contrastText,
                bgcolor: theme.palette.primary.main,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                mb: 2,
              }}
            >
              Work Order Information
            </Typography>

            <Grid container spacing={2}>
              {/* Customer - With Search */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <Controller
                    name="customerIdentity"
                    control={control}
                    render={({ field }) => (
                      <Box
                        sx={{ position: "relative" }}
                        ref={customerSearchRef}
                      >
                        <TextField
                          {...field}
                          value={
                            watchCustomerIdentity
                              ? customerSearchTerm
                              : customerSearchTerm
                          }
                          onChange={(e) => {
                            field.onChange(null);
                            handleCustomerSearchChange(e.target.value);
                          }}
                          onFocus={() => {
                            if (customers.length > 0) {
                              setShowCustomerDropdown(true);
                            }
                          }}
                          label={<RequiredLabel label="Customer" />}
                          error={!!errors.customerIdentity}
                          helperText={getErrorMessage("customerIdentity")}
                          size="small"
                          fullWidth
                          placeholder="Search customer..."
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {customerSearchLoading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <SearchIcon />
                                  )}
                                </Stack>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {showCustomerDropdown && customers.length > 0 && (
                          <List
                            sx={searchCustomerListStyleTechnicianCalendar(
                              theme,
                            )}
                          >
                            {customers.map((customer) => (
                              <ListItem
                                sx={searchListItem(theme)}
                                key={customer.identity}
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                {customer.name} / {customer.number}
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Location */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="locationIdentity"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={locationOptions}
                      size="small"
                      getOptionLabel={(option) => option.displayText}
                      value={
                        locationOptions.find(
                          (l) => l.identity === field.value,
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        field.onChange(newValue?.identity || null);
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: 150,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={<RequiredLabel label="Location" />}
                          error={!!errors.locationIdentity}
                          helperText={getErrorMessage("locationIdentity")}
                          size="small"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.woStatusIdentity}
                >
                  <InputLabel>
                    <RequiredLabel label="Status" />
                  </InputLabel>
                  <Controller
                    name="woStatusIdentity"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value || ""}
                        label="Status"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                            },
                          },
                        }}
                      >
                        {statusOptions.map((status) => (
                          <MenuItem
                            key={status.identity}
                            value={status.identity}
                          >
                            {status.displayText}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Priority */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.woPriorityIdentity}
                >
                  <InputLabel>
                    <RequiredLabel label="Priority" />
                  </InputLabel>
                  <Controller
                    name="woPriorityIdentity"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value || ""}
                        label="Priority"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                            },
                          },
                        }}
                      >
                        {priorityOptions.map((priority) => (
                          <MenuItem
                            key={priority.identity}
                            value={priority.identity}
                          >
                            {priority.displayText}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Sales Person */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="salesPersonIdentity"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={salesPersonOptions}
                      size="small"
                      getOptionLabel={(option) => option.displayText}
                      value={
                        salesPersonOptions.find(
                          (sp) => sp.identity === field.value,
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        field.onChange(newValue?.identity || null);
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: 150,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={<RequiredLabel label="Sales Person" />}
                          error={!!errors.salesPersonIdentity}
                          helperText={getErrorMessage("salesPersonIdentity")}
                          size="small"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Technician (Optional) */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="technicianIdentity"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={technicianOptions}
                      size="small"
                      getOptionLabel={(option) => option.displayText}
                      value={
                        technicianOptions.find(
                          (t) => t.identity === field.value,
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        field.onChange(newValue?.identity || null);
                      }}
                      ListboxProps={{
                        style: {
                          maxHeight: 150,
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Technician"
                          size="small"
                          placeholder="Optional"
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Section 2: Segment Mandatory Info */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.contrastText,
                bgcolor: theme.palette.primary.main,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                mb: 2,
              }}
            >
              Segment Information
            </Typography>

            <Grid container spacing={2}>
              {/* Segment Title */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="segment.displayText"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label={<RequiredLabel label="Segment Title" />}
                      error={!!errors.segment?.displayText}
                      helperText={getErrorMessage("segment.displayText")}
                      inputProps={{ maxLength: 15 }}
                    />
                  )}
                />
              </Grid>

              {/* Service Code */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.segment?.serviceCodeIdentity}
                >
                  <InputLabel>
                    <RequiredLabel label="Service Code" />
                  </InputLabel>
                  <Controller
                    name="segment.serviceCodeIdentity"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value || ""}
                        label="Service Code"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 150,
                            },
                          },
                        }}
                      >
                        {serviceCodeOptions.map((code) => (
                          <MenuItem key={code.identity} value={code.identity}>
                            {code.displayText}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      {/* Dialog Actions - Following WorkOrderDialogContainerMain.tsx pattern */}
      <DialogActions
        sx={{
          padding: "16px 24px",
          justifyContent: "flex-end",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" gap={1}>
          {/* Save Draft Button */}
          <Button
            sx={{ ...ButtonStyleOutlined }}
            size="small"
            variant="outlined"
            onClick={handleSubmit((data) => onSubmit(data, "draft"))}
            disabled={isSubmitting || createWorkOrderMutation.isPending}
          >
            Save Draft
          </Button>

          {/* Commit Button */}
          <Button
            sx={{ ...ButtonStyle }}
            size="small"
            variant="contained"
            onClick={handleSubmit((data) => onSubmit(data, "commit"))}
            disabled={isSubmitting || createWorkOrderMutation.isPending}
          >
            Commit
          </Button>
        </Box>
      </DialogActions>

      {/* Loading Backdrop with JumpingLoader */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        open={createWorkOrderMutation.isPending} //createWorkOrderMutation.isPending
      >
        <JumpingLoader message="Creating work order..." fullScreen={true} />
      </Backdrop>
    </Dialog>
  );
};

export default SmartWoCreationDialog;
